import type {
  AdminProfileCardSet,
  AdminProfileCardSetWithSlots,
  AdminProfileCardSlot,
  ProfileCardRarity,
  WorkspaceProfileCardSetSetting,
} from '../types/profileCard';
import { parseProfileCardRarity, MAX_PROFILE_CARD_SLOTS_PER_SET } from '../types/profileCard';
import { IMAGE_WEBP_MIME } from '../utils/imageCompress';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  buildAdminProfileCardCatalogVersion,
  buildProfileCardRowVersion,
  buildWorkspaceSettingsVersion,
  invalidateAdminProfileCardCatalogCache,
  invalidateWorkspaceProfileCardSettingsCache,
  loadAdminProfileCardSetsCache,
  loadWorkspaceProfileCardSettingsCache,
  saveAdminProfileCardSetsCache,
  saveWorkspaceProfileCardSettingsCache,
} from '../utils/profileCardCatalogCache';
import { supabase } from '../supabaseClient';

const BUCKET = 'profile-card-images';

function isMissingTableError(message: string | undefined): boolean {
  return /does not exist|schema cache/i.test(message ?? '');
}

function mapSetRow(row: Record<string, unknown>): AdminProfileCardSet {
  return {
    id: String(row.id ?? ''),
    name: typeof row.name === 'string' ? row.name : '',
    sortOrder: Number(row.sort_order) || 0,
    createdAt: typeof row.created_at === 'string' ? row.created_at : '',
    updatedAt: typeof row.updated_at === 'string' ? row.updated_at : '',
  };
}

function mapSlotRow(row: Record<string, unknown>): AdminProfileCardSlot {
  const storagePath = typeof row.storage_path === 'string' ? row.storage_path : null;
  const parsed = parseProfileCardRarity(row.rarity);
  // 마이그레이션 전/미적용: drop_weight>0 + 이미지 → 보통으로 취급
  const rarity: ProfileCardRarity | null =
    parsed ??
    (storagePath && Number(row.drop_weight) > 0 ? 'normal' : null);
  return {
    id: String(row.id ?? ''),
    setId: String(row.set_id ?? ''),
    slotIndex: Number(row.slot_index) || 0,
    name: typeof row.name === 'string' ? row.name.trim() : '',
    storagePath,
    imageVersion: Number(row.image_version) || 0,
    rarity,
    updatedAt: typeof row.updated_at === 'string' ? row.updated_at : '',
  };
}

/** 관리자 카탈로그 head — id/updated_at/image_version 등 메타만 */
export async function fetchAdminProfileCardSetsHeadVersion(): Promise<string> {
  const [setsRes, slotsRes] = await Promise.all([
    supabase.from('admin_profile_card_sets').select('id, updated_at, sort_order'),
    supabase
      .from('admin_profile_card_slots')
      .select('id, updated_at, image_version, rarity, storage_path'),
  ]);
  if (setsRes.error) {
    if (isMissingTableError(setsRes.error.message)) return 'missing';
    throw new Error(setsRes.error.message);
  }
  if (slotsRes.error && !isMissingTableError(slotsRes.error.message)) {
    throw new Error(slotsRes.error.message);
  }
  const setPart = buildProfileCardRowVersion(
    ((setsRes.data ?? []) as Record<string, unknown>[]).map((r) => ({
      id: String(r.id ?? ''),
      updated_at: String(r.updated_at ?? ''),
      extra: Number(r.sort_order) || 0,
    }))
  );
  const slotPart = buildProfileCardRowVersion(
    ((slotsRes.data ?? []) as Record<string, unknown>[]).map((r) => ({
      id: String(r.id ?? ''),
      updated_at: String(r.updated_at ?? ''),
      extra: `${Number(r.image_version) || 0}:${r.rarity ?? ''}:${r.storage_path ? '1' : '0'}`,
    }))
  );
  return `sets:${setPart}||slots:${slotPart}`;
}

/** 관리자 카탈로그: 모든 세트 + 세트별 슬롯(동적 개수) — 네트워크 풀 로드 */
export async function fetchAdminProfileCardSets(): Promise<AdminProfileCardSetWithSlots[]> {
  const [setsRes, slotsRes] = await Promise.all([
    supabase.from('admin_profile_card_sets').select('*').order('sort_order', { ascending: true }),
    supabase.from('admin_profile_card_slots').select('*').order('slot_index', { ascending: true }),
  ]);
  if (setsRes.error) {
    if (isMissingTableError(setsRes.error.message)) return [];
    throw new Error(setsRes.error.message);
  }
  if (slotsRes.error && !isMissingTableError(slotsRes.error.message)) {
    throw new Error(slotsRes.error.message);
  }
  const slotsBySet = new Map<string, AdminProfileCardSlot[]>();
  ((slotsRes.data ?? []) as Record<string, unknown>[]).map(mapSlotRow).forEach((slot) => {
    const list = slotsBySet.get(slot.setId) ?? [];
    list.push(slot);
    slotsBySet.set(slot.setId, list);
  });
  return ((setsRes.data ?? []) as Record<string, unknown>[]).map(mapSetRow).map((set) => ({
    ...set,
    slots: slotsBySet.get(set.id) ?? [],
  }));
}

/**
 * 관리자 카탈로그: LS 캐시 → head 버전 비교 → 동일하면 풀 fetch 생략.
 * 로컬 뮤테이션 직후에는 `forceRefresh: true`.
 */
export async function getAdminProfileCardSets(options?: {
  forceRefresh?: boolean;
}): Promise<AdminProfileCardSetWithSlots[]> {
  const cached = loadAdminProfileCardSetsCache();
  if (!options?.forceRefresh && cached) {
    try {
      const head = await fetchAdminProfileCardSetsHeadVersion();
      if (head === cached.version) {
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('프로필카드·관리자세트·LS');
        }
        return cached.sets;
      }
    } catch {
      /* head 실패 시 풀 로드 */
    }
  }
  const sets = await fetchAdminProfileCardSets();
  saveAdminProfileCardSetsCache(sets, buildAdminProfileCardCatalogVersion(sets));
  return sets;
}

/** 세트 생성(기본 10개 빈 슬롯 — 이후 카드 추가로 더 늘릴 수 있음) */
export async function createAdminProfileCardSet(name: string, sortOrder = 0): Promise<string> {
  const { data, error } = await supabase.rpc('create_admin_profile_card_set', {
    p_name: name,
    p_sort_order: sortOrder,
  });
  if (error) throw new Error(error.message);
  invalidateAdminProfileCardCatalogCache();
  return String(data);
}

/** 세트에 카드 슬롯 1개 추가(다음 번호) */
export async function addAdminProfileCardSlot(setId: string): Promise<AdminProfileCardSlot> {
  const { data: existingCountRows, error: countErr } = await supabase
    .from('admin_profile_card_slots')
    .select('slot_index')
    .eq('set_id', setId);
  if (countErr) throw new Error(countErr.message);
  if ((existingCountRows ?? []).length >= MAX_PROFILE_CARD_SLOTS_PER_SET) {
    throw new Error(`프로필 카드는 세트당 최대 ${MAX_PROFILE_CARD_SLOTS_PER_SET}개까지 등록할 수 있습니다.`);
  }

  const { data, error } = await supabase.rpc('add_admin_profile_card_slot', {
    p_set_id: setId,
  });
  if (!error && data) {
    invalidateAdminProfileCardCatalogCache();
    return mapSlotRow(data as Record<string, unknown>);
  }
  if (error && !/does not exist|schema cache|Could not find/i.test(error.message ?? '')) {
    throw new Error(error.message);
  }
  // RPC 미적용 환경 폴백: 다음 slot_index로 직접 insert
  const { data: existing, error: selErr } = await supabase
    .from('admin_profile_card_slots')
    .select('slot_index')
    .eq('set_id', setId);
  if (selErr) throw new Error(selErr.message);
  if ((existing ?? []).length >= MAX_PROFILE_CARD_SLOTS_PER_SET) {
    throw new Error(`프로필 카드는 세트당 최대 ${MAX_PROFILE_CARD_SLOTS_PER_SET}개까지 등록할 수 있습니다.`);
  }
  const nextIndex =
    Math.max(0, ...((existing ?? []) as Array<{ slot_index?: number }>).map((r) => Number(r.slot_index) || 0)) +
    1;
  if (nextIndex > MAX_PROFILE_CARD_SLOTS_PER_SET) {
    throw new Error(`프로필 카드는 세트당 최대 ${MAX_PROFILE_CARD_SLOTS_PER_SET}개까지 등록할 수 있습니다.`);
  }
  const { data: inserted, error: insErr } = await supabase
    .from('admin_profile_card_slots')
    .insert({ set_id: setId, slot_index: nextIndex, drop_weight: 0, rarity: null })
    .select('*')
    .single();
  if (insErr) throw new Error(insErr.message);
  invalidateAdminProfileCardCatalogCache();
  return mapSlotRow(inserted as Record<string, unknown>);
}

/** 슬롯(카드) 전체 삭제 — Storage 이미지·멤버 보유 이력 포함 */
export async function deleteAdminProfileCardSlot(setId: string, slotIndex: number): Promise<void> {
  const storagePath = `${setId}/${slotIndex}.webp`;
  await supabase.storage.from(BUCKET).remove([storagePath]);
  await supabase
    .from('class_member_profile_cards')
    .delete()
    .eq('set_id', setId)
    .eq('slot_index', slotIndex)
    .eq('source_kind', 'admin');
  const { error } = await supabase
    .from('admin_profile_card_slots')
    .delete()
    .eq('set_id', setId)
    .eq('slot_index', slotIndex);
  if (error) throw new Error(error.message);
  invalidateAdminProfileCardCatalogCache();
}

export async function renameAdminProfileCardSet(setId: string, name: string): Promise<void> {
  const { error } = await supabase
    .from('admin_profile_card_sets')
    .update({ name: name.trim(), updated_at: new Date().toISOString() })
    .eq('id', setId);
  if (error) throw new Error(error.message);
  invalidateAdminProfileCardCatalogCache();
}

export async function deleteAdminProfileCardSet(setId: string): Promise<void> {
  const { data: files } = await supabase.storage.from(BUCKET).list(setId);
  const paths = ((files ?? []) as Array<{ name: string }>).map((f) => `${setId}/${f.name}`);
  if (paths.length > 0) {
    await supabase.storage.from(BUCKET).remove(paths);
  }
  const { error } = await supabase.from('admin_profile_card_sets').delete().eq('id', setId);
  if (error) throw new Error(error.message);
  invalidateAdminProfileCardCatalogCache();
}

export async function updateSlotRarity(
  slotId: string,
  rarity: ProfileCardRarity | null,
  name?: string | null
): Promise<void> {
  const trimmed = typeof name === 'string' ? name.trim() : undefined;
  const { error } = await supabase
    .from('admin_profile_card_slots')
    .update({
      rarity,
      drop_weight: rarity ? 1 : 0,
      ...(trimmed !== undefined ? { name: trimmed || null } : {}),
      updated_at: new Date().toISOString(),
    })
    .eq('id', slotId);
  if (error) throw new Error(error.message);
  invalidateAdminProfileCardCatalogCache();
}

/** @deprecated updateSlotRarity 사용 */
export async function updateSlotDropWeight(slotId: string, dropWeight: number): Promise<void> {
  await updateSlotRarity(slotId, dropWeight > 0 ? 'normal' : null);
}

export type UploadSlotImageResult = { storagePath: string; imageVersion: number };

/** 슬롯 이미지 업로드(webp, 크롭된 blob) — 같은 슬롯 재업로드 시 덮어쓰고 버전만 올림 */
export async function uploadProfileCardSlotImage(
  setId: string,
  slotIndex: number,
  blob: Blob
): Promise<UploadSlotImageResult> {
  const storagePath = `${setId}/${slotIndex}.webp`;
  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(storagePath, blob, {
    contentType: IMAGE_WEBP_MIME,
    cacheControl: '31536000',
    upsert: true,
  });
  if (uploadErr) throw uploadErr;

  const imageVersion = Date.now();
  const { error } = await supabase
    .from('admin_profile_card_slots')
    .update({ storage_path: storagePath, image_version: imageVersion, updated_at: new Date().toISOString() })
    .eq('set_id', setId)
    .eq('slot_index', slotIndex);
  if (error) throw new Error(error.message);
  invalidateAdminProfileCardCatalogCache();

  return { storagePath, imageVersion };
}

export async function removeProfileCardSlotImage(setId: string, slotIndex: number): Promise<void> {
  const storagePath = `${setId}/${slotIndex}.webp`;
  await supabase.storage.from(BUCKET).remove([storagePath]);
  const { error } = await supabase
    .from('admin_profile_card_slots')
    .update({
      storage_path: null,
      drop_weight: 0,
      rarity: null,
      updated_at: new Date().toISOString(),
    })
    .eq('set_id', setId)
    .eq('slot_index', slotIndex);
  if (error) throw new Error(error.message);
  invalidateAdminProfileCardCatalogCache();
}

export function profileCardImagePublicUrl(storagePath: string, imageVersion?: number): string {
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  const base = data.publicUrl;
  return imageVersion ? `${base}?v=${imageVersion}` : base;
}

// --- 워크스페이스별 세트 활성화 + 가격 -------------------------------------

function mapWorkspaceSettingRow(row: Record<string, unknown>): WorkspaceProfileCardSetSetting {
  return {
    id: String(row.id ?? ''),
    workspaceId: String(row.workspace_id ?? ''),
    setId: String(row.set_id ?? ''),
    enabled: row.enabled === true,
    priceCoins: Number(row.price_coins) || 0,
    updatedAt: typeof row.updated_at === 'string' ? row.updated_at : '',
  };
}

export async function fetchWorkspaceProfileCardSettingsHeadVersion(
  workspaceId: string
): Promise<string> {
  if (!workspaceId) return 'empty';
  const { data, error } = await supabase
    .from('workspace_profile_card_set_settings')
    .select('set_id, enabled, price_coins, updated_at')
    .eq('workspace_id', workspaceId);
  if (error) {
    if (isMissingTableError(error.message)) return 'missing';
    throw new Error(error.message);
  }
  return buildProfileCardRowVersion(
    ((data ?? []) as Record<string, unknown>[]).map((r) => ({
      id: String(r.set_id ?? ''),
      updated_at: String(r.updated_at ?? ''),
      extra: `${r.enabled === true ? '1' : '0'}:${Number(r.price_coins) || 0}`,
    }))
  );
}

export async function fetchWorkspaceProfileCardSettings(
  workspaceId: string
): Promise<WorkspaceProfileCardSetSetting[]> {
  if (!workspaceId) return [];
  const { data, error } = await supabase
    .from('workspace_profile_card_set_settings')
    .select('*')
    .eq('workspace_id', workspaceId);
  if (error) {
    if (isMissingTableError(error.message)) return [];
    throw new Error(error.message);
  }
  return ((data ?? []) as Record<string, unknown>[]).map(mapWorkspaceSettingRow);
}

/** 워크스페이스의 관리자 세트 활성/가격 설정 — head 비교 후 캐시 재사용 */
export async function getWorkspaceProfileCardSettings(
  workspaceId: string,
  options?: { forceRefresh?: boolean }
): Promise<WorkspaceProfileCardSetSetting[]> {
  if (!workspaceId) return [];
  const cached = loadWorkspaceProfileCardSettingsCache(workspaceId);
  if (!options?.forceRefresh && cached) {
    try {
      const head = await fetchWorkspaceProfileCardSettingsHeadVersion(workspaceId);
      if (head === cached.version) {
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('프로필카드·관리자설정·LS');
        }
        return cached.settings;
      }
    } catch {
      /* fall through */
    }
  }
  const settings = await fetchWorkspaceProfileCardSettings(workspaceId);
  saveWorkspaceProfileCardSettingsCache(
    workspaceId,
    settings,
    buildWorkspaceSettingsVersion(settings)
  );
  return settings;
}

// --- 멤버 보유(뽑기 결과) 조회: 프로필 이미지 선택 모달에서 잠금 여부 판단용 -----

/** 특정 멤버가 보유한 슬롯 번호 목록(세트별). 프로필 이미지 선택 모달에서만 사용 */
export async function fetchMemberProfileCardUnlocks(
  memberId: string
): Promise<Array<{ setId: string; slotIndex: number; sourceKind: 'admin' | 'workspace' }>> {
  if (!memberId) return [];
  const { data, error } = await supabase
    .from('class_member_profile_cards')
    .select('set_id, slot_index, source_kind')
    .eq('class_member_id', memberId);
  if (error) {
    if (/does not exist|schema cache/i.test(error.message ?? '')) return [];
    throw new Error(error.message);
  }
  return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
    setId: String(row.set_id ?? ''),
    slotIndex: Number(row.slot_index) || 0,
    sourceKind: row.source_kind === 'workspace' ? 'workspace' : 'admin',
  }));
}

export type WorkspaceProfileCardSettingInput = {
  setId: string;
  enabled: boolean;
  priceCoins: number;
};

/** 워크스페이스가 사용할 세트 선택 + 가격을 한 번에 저장(없으면 생성) */
export async function saveWorkspaceProfileCardSettings(
  workspaceId: string,
  settings: WorkspaceProfileCardSettingInput[]
): Promise<void> {
  if (!workspaceId || settings.length === 0) return;
  const rows = settings.map((s) => ({
    workspace_id: workspaceId,
    set_id: s.setId,
    enabled: s.enabled,
    price_coins: Math.max(0, Math.round(s.priceCoins) || 0),
    updated_at: new Date().toISOString(),
  }));
  const { error } = await supabase
    .from('workspace_profile_card_set_settings')
    .upsert(rows, { onConflict: 'workspace_id,set_id' });
  if (error) throw new Error(error.message);
  invalidateWorkspaceProfileCardSettingsCache(workspaceId);
}
